'use client';

import UiIcon from '@/components/ui/UiIcon';
import React, { useState, useCallback } from 'react';
import { Category, Tag } from '@/lib/api';

interface NewsFiltersProps {
  categories: Category[];
  tags: Tag[];
  selectedCategory: string;
  selectedTag: string;
  searchQuery: string;
  onCategoryChange: (category: string) => void;
  onTagChange: (tag: string) => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
  className?: string;
}

export default function NewsFilters({
  categories,
  tags,
  selectedCategory,
  selectedTag,
  searchQuery,
  onCategoryChange,
  onTagChange,
  onSearchChange,
  onClearFilters,
  className = ''
}: NewsFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQuery);

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(searchInput.trim());
  }, [searchInput, onSearchChange]);

  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    onSearchChange('');
  }, [onSearchChange]);

  const hasActiveFilters = selectedCategory || selectedTag || searchQuery;

  return (
    <div className={`bg-site-surface/50 backdrop-blur-md border border-site-border-control/50 rounded-lg p-4 ${className}`}>
      {/* Поиск */}
      <form onSubmit={handleSearchSubmit} className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Поиск по новостям..."
            className="w-full px-4 py-2 pl-10 bg-site-raised/50 border border-site-border-control/50 rounded-lg text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent focus:border-transparent"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-site-muted">
            <UiIcon name="MagnifyingGlass" />
          </div>
          {searchInput && (
            <button
              type="button"
              onClick={handleSearchClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-site-muted hover:text-site-text transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      </form>

      {/* Кнопка показать/скрыть фильтры */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-site-secondary hover:text-site-text transition-colors"
        >
          <span className="text-lg"><UiIcon name="Wrench" /></span>
          <span>Фильтры</span>
          <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
        
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-site-accent hover:text-site-accent transition-colors"
          >
            Очистить все
          </button>
        )}
      </div>

      {/* Фильтры */}
      {isExpanded && (
        <div className="space-y-4">
          {/* Категории */}
          {categories.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-site-secondary mb-2">Категории</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onCategoryChange('')}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    !selectedCategory
                      ? 'bg-site-accent text-site-on-accent'
                      : 'bg-site-raised/50 text-site-secondary hover:bg-site-raised/50'
                  }`}
                >
                  Все
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => onCategoryChange(category.slug)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedCategory === category.slug
                        ? 'text-site-text'
                        : 'bg-site-raised/50 text-site-secondary hover:bg-site-raised/50'
                    }`}
                    style={{
                      backgroundColor: selectedCategory === category.slug ? category.color : undefined
                    }}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Теги */}
          {tags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-site-secondary mb-2">Теги</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onTagChange('')}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    !selectedTag
                      ? 'bg-site-accent-secondary text-site-on-accent'
                      : 'bg-site-raised/50 text-site-secondary hover:bg-site-raised/50'
                  }`}
                >
                  Все
                </button>
                {tags.slice(0, 10).map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => onTagChange(tag.slug)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTag === tag.slug
                        ? 'bg-site-accent-secondary text-site-on-accent'
                        : 'bg-site-raised/50 text-site-secondary hover:bg-site-raised/50'
                    }`}
                  >
                    #{tag.name}
                  </button>
                ))}
                {tags.length > 10 && (
                  <span className="px-3 py-1 text-site-muted text-sm">
                    +{tags.length - 10} еще
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Активные фильтры */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-site-border-control/50">
          <div className="flex flex-wrap gap-2">
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-site-accent/20 text-site-accent text-xs rounded-full">
                Категория: {categories.find(c => c.slug === selectedCategory)?.name}
                <button
                  onClick={() => onCategoryChange('')}
                  className="hover:text-site-accent"
                >
                  ✕
                </button>
              </span>
            )}
            {selectedTag && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-site-accent-secondary/20 text-site-accent-secondary text-xs rounded-full">
                Тег: #{tags.find(t => t.slug === selectedTag)?.name}
                <button
                  onClick={() => onTagChange('')}
                  className="hover:text-site-accent-secondary"
                >
                  ✕
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-site-success/20 text-site-success text-xs rounded-full">
                Поиск: "{searchQuery}"
                <button
                  onClick={handleSearchClear}
                  className="hover:text-site-success"
                >
                  ✕
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

